import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/hooks/store/useAuth";
import { Form } from "@/components/Base/Form";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Button } from "@/components/Base/Button";
import { AuthFormHeader } from "@/components/Auth/AuthFormHeader";
import { FieldType, InputFields } from "@/components/Common/form/InputFields";
import { captureException } from "@/services/sentry";

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email" }),
  password: z
    .string()
    .min(8, { message: "Password should be of minimum 8 characters length" }),
  confirmPassword: z
    .string()
    .min(1, { message: "Password confirmation is required" }),
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
});

const finalSchema = formSchema.refine(
  (data) => data.confirmPassword === data.password,
  {
    message: "Passwords don't match",
    path: ["confirmPassword"], // This targets the error at the confirmPassword field
  }
);

const fields: FieldType<z.infer<typeof finalSchema>>[] = [
  {
    name: "firstName",
    label: "First Name",
    type: "text",
    placeholder: "First Name",
    required: true,
    autoComplete: "given-name",
  },
  {
    name: "lastName",
    label: "Last Name",
    type: "text",
    required: true,
    placeholder: "Last Name",
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    placeholder: "Email",
    required: true,
    autoComplete: "email",
    autoFocus: true,
  },
  {
    name: "password",
    label: "Password",
    type: "password",
    placeholder: "Password",
    required: true,
    autoComplete: "new-password",
  },
  {
    name: "confirmPassword",
    label: "Confirm Password",
    type: "password",
    placeholder: "Confirm Password",
    required: true,
  },
];

const initialValues = {
  email: "",
  password: "",
  confirmPassword: "",
  firstName: "",
  lastName: "",
};

export const RegisterForm = ({ onSubmit }: { onSubmit: () => void }) => {
  const form = useForm<z.infer<typeof finalSchema>>({
    resolver: zodResolver(finalSchema),
    defaultValues: initialValues,
  });

  const { authError, register } = useAuth();

  const handleSubmit = async ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    confirmPassword,
    ...values
  }: z.infer<typeof finalSchema>) => {
    try {
      await register({ ...values });
      onSubmit();
    } catch (error) {
      captureException(error);
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex min-h-full items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-md space-y-2 rounded-xl border-2 border-white bg-secondary/10 p-8">
            <AuthFormHeader title="Create Your Account" />
            <div className="mt-6 flex flex-col justify-center gap-2"></div>
            <InputFields fields={fields} form={form} />

            <div className={cn("flex w-96 pb-4", !authError && "invisible")}>
              <div className="w-128 text-destructive">{authError}</div>
            </div>

            <div className={"flex flex-row justify-between"}>
              <Link to="/resetpassword">
                <Button type={"button"}>Forgot Password</Button>
              </Link>
              <Button
                variant={"accent"}
                type={"submit"}
                disabled={
                  form.formState.isSubmitting || form.formState.isSubmitted
                }
              >
                {form.formState.isSubmitting ? "Submitting" : "Register"}
              </Button>
            </div>
            <div className={"flex flex-row justify-center pt-4"}>
              <p>
                Already have an account?{" "}
                <Link to="/login" className={"underline"}>
                  Login
                </Link>{" "}
                to get back to working.
              </p>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
